import { useSearchParams } from "react-router-dom";
import {
  ProgramCategoryEnum,
  ServiceCategoryEnum,
  ServiceContract,
  ServiceRequest,
  ServiceProgram,
} from "../types/service.type";

import { useProfileData } from "../../../hooks/useProfileData";

import { useOrderedRequestsInfinite } from "../api/queries/useOrderedRequests";
import { useReceivedRequestsInfinite } from "../api/queries/useReceivedRequests";
import { useOrderedContractsInfinite } from "../api/queries/useOrderedContracts";
import { useReceivedContractsInfinite } from "../api/queries/useReceivedContracts";
import { useReceivedProgramRequestsInfinite } from "../api/queries/useRecievedProgramRequests";
import { useOrderedProgramRequestsInfinite } from "../api/queries/useOrderedProgramRequests";

interface UseServiceRequestsResult {
  requests?: ServiceRequest[];
  programs?: ServiceProgram[];
  contracts?: ServiceContract[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

export const useServiceRequests = (): UseServiceRequestsResult => {
  const [searchParams] = useSearchParams();
  const requestType = searchParams.get("request-type") || "received";
  const category = (searchParams.get("category") || "new") as ServiceCategoryEnum | ProgramCategoryEnum;
  const type = searchParams.get("type");
  const isProgram = type === "programs";

  const { data: userData, isLoading: isProfileLoading } = useProfileData();
  const { id } = userData || {};

  const shouldFetchReceivedRequests =
    (category === "new" || category === "history") && requestType === "received" && !isProgram;
  const shouldFetchOrderedRequests =
    (category === "new" || category === "history") && requestType === "ordered" && !isProgram;
  const shouldFetchReceivedContracts = category === "contract" && requestType === "received";
  const shouldFetchOrderedContracts = category === "contract" && requestType === "ordered";
  const shouldFetchReceivedPrograms = category !== "my-orders" && isProgram;
  const shouldFetchOrderedPrograms = category === "my-orders" && isProgram;

  const {
    data: receivedRequests,
    isLoading: isReceivedRequestsLoading,
    hasNextPage: hasNextReceivedRequestsPage,
    isFetchingNextPage: isFetchingNextReceivedRequestsPage,
    fetchNextPage: fetchNextReceivedRequestsPage,
  } = useReceivedRequestsInfinite({
    category: category as ServiceCategoryEnum,
    enabled: shouldFetchReceivedRequests,
  });

  const {
    data: orderedRequests,
    isLoading: isOrderedRequestsLoading,
    hasNextPage: hasNextOrderedRequestsPage,
    isFetchingNextPage: isFetchingNextOrderedRequestsPage,
    fetchNextPage: fetchNextOrderedRequestsPage,
  } = useOrderedRequestsInfinite({
    category: category as ServiceCategoryEnum,
    enabled: shouldFetchOrderedRequests,
  });

  const {
    data: receivedPrograms,
    isLoading: isReceivedProgramsLoading,
    hasNextPage: hasNextReceivedProgramsPage,
    isFetchingNextPage: isFetchingNextReceivedProgramsPage,
    fetchNextPage: fetchNextReceivedProgramsPage,
  } = useReceivedProgramRequestsInfinite({
    enabled: shouldFetchReceivedPrograms,
  });

  const {
    data: orderedPrograms,
    isLoading: isOrderedProgramsLoading,
    hasNextPage: hasNextOrderedProgramsPage,
    isFetchingNextPage: isFetchingNextOrderedProgramsPage,
    fetchNextPage: fetchNextOrderedProgramsPage,
  } = useOrderedProgramRequestsInfinite({
    enabled: shouldFetchOrderedPrograms,
  });

  const {
    data: receivedContracts,
    isLoading: isReceivedContractsLoading,
    hasNextPage: hasNextReceivedContractsPage,
    isFetchingNextPage: isFetchingNextReceivedContractsPage,
    fetchNextPage: fetchNextReceivedContractsPage,
  } = useReceivedContractsInfinite({
    category: category as ServiceCategoryEnum,
    enabled: shouldFetchReceivedContracts && !!id,
    userId: String(id),
  });

  const {
    data: orderedContracts,
    isLoading: isOrderedContractsLoading,
    hasNextPage: hasNextOrderedContractsPage,
    isFetchingNextPage: isFetchingNextOrderedContractsPage,
    fetchNextPage: fetchNextOrderedContractsPage,
  } = useOrderedContractsInfinite({
    category: category as ServiceCategoryEnum,
    enabled: shouldFetchOrderedContracts && !!id,
    userId: String(id),
  });

  const requests = !isProgram
    ? requestType === "received"
      ? receivedRequests?.pages.flatMap((page) => page.items)
      : orderedRequests?.pages.flatMap((page) => page.data.items)
    : undefined;

  const programs = isProgram
    ? category === "my-orders"
      ? orderedPrograms?.pages.flatMap((page) => page.data?.items)
      : receivedPrograms?.pages.flatMap((page) => page.data?.items)
    : undefined;

  const contracts =
    !isProgram && category === "contract"
      ? requestType === "received"
        ? receivedContracts?.pages.flatMap((page) => page.data.items)
        : orderedContracts?.pages.flatMap((page) => page.data.items)
      : undefined;

  const isLoading =
    isProfileLoading ||
    isReceivedRequestsLoading ||
    isOrderedRequestsLoading ||
    isReceivedContractsLoading ||
    isOrderedContractsLoading ||
    isReceivedProgramsLoading ||
    isOrderedProgramsLoading;

  const hasNextPage =
    requestType === "received"
      ? category === "contract"
        ? hasNextReceivedContractsPage
        : isProgram
        ? hasNextReceivedProgramsPage
        : hasNextReceivedRequestsPage
      : category === "contract"
      ? hasNextOrderedContractsPage
      : isProgram
      ? hasNextOrderedProgramsPage
      : hasNextOrderedRequestsPage;

  const isFetchingNextPage =
    requestType === "received"
      ? category === "contract"
        ? isFetchingNextReceivedContractsPage
        : isProgram
        ? isFetchingNextReceivedProgramsPage
        : isFetchingNextReceivedRequestsPage
      : category === "contract"
      ? isFetchingNextOrderedContractsPage
      : isProgram
      ? isFetchingNextOrderedProgramsPage
      : isFetchingNextOrderedRequestsPage;

  const fetchNextPage = () => {
    if (requestType === "received") {
      if (category === "contract") {
        fetchNextReceivedContractsPage();
      } else if (isProgram) {
        fetchNextReceivedProgramsPage();
      } else {
        fetchNextReceivedRequestsPage();
      }
    } else {
      if (category === "contract") {
        fetchNextOrderedContractsPage();
      } else if (isProgram) {
        fetchNextOrderedProgramsPage();
      } else {
        fetchNextOrderedRequestsPage();
      }
    }
  };

  return {
    requests,
    programs,
    contracts,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  };
};
